import { Box, Button, Typography, Card, Paper, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useState } from "react";
import { colors, alphaVar } from "../../../../../ui/colors";

interface BankDetails {
  accountNumber?: string;
  routingNumber?: string;
  bankName?: string;
  accountHolder?: string;
  swift?: string;
  iban?: string;
}

interface ManualPaymentInstructionsProps {
  bankDetails?: BankDetails;
  referenceNumber?: string;
  onBackClick?: () => void;
}

export const ManualPaymentInstructions = ({ bankDetails, referenceNumber, onBackClick }: ManualPaymentInstructionsProps) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const CopyableField = ({ label, value, fieldId }: { label: string; value: string; fieldId: string }) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="caption"
        sx={{ color: colors.gray[400], fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", lineHeight: 1.5 }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1,

          bgcolor: "var(--fx-surface-base, #FFFFFF)",
          borderRadius: 2,
          border: `1px solid ${alphaVar(colors.gray[300], 0.5)}`,
        }}
      >
        <Typography
          sx={{
            color: "var(--fx-surface-textsecondary, #64748B)",
            flex: 1,
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.5,
          }}
        >
          {value}
        </Typography>
        <IconButton
          size="small"
          onClick={() => copyToClipboard(value, fieldId)}
          sx={{
            color: colors.orange.base,
            padding: "4px",
            "&:hover": {
              bgcolor: alphaVar(colors.orange.base, 0.1),
            },
          }}
        >
          {copiedField === fieldId ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : <FileCopyIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Card
      sx={{
        p: 2,
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        border: "none",
        borderRadius: 4,
        display: "flex",
        flexDirection: "column",
        minHeight: "280px",
        boxShadow: "none",
      }}
    >
      {/* Back button */}
      {onBackClick && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBackClick}
          sx={{
            mb: 2,
            color: colors.gray[600],
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 500,
            padding: 0,
            alignSelf: "flex-start",
            "&:hover": {
              bgcolor: "transparent",
              color: colors.orange.base,
            },
          }}
        >
          Back to payment methods
        </Button>
      )}

      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: colors.gray[600], mb: 0.5 }}>
          Bank Transfer Payment
        </Typography>
        <Typography variant="caption" sx={{ color: colors.gray[500], fontSize: "0.75rem" }}>
          Use the details below to transfer
        </Typography>
      </Box>

      {/* Bank Details - Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          pr: 1,
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: alphaVar(colors.gray[200], 0.2),
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: alphaVar(colors.gray[400], 0.5),
            borderRadius: "10px",
            "&:hover": {
              bgcolor: alphaVar(colors.gray[600], 0.7),
            },
          },
        }}
      >
        {bankDetails?.bankName && <CopyableField label="Bank Name" value={bankDetails.bankName} fieldId="bankName" />}
        {bankDetails?.accountHolder && <CopyableField label="Account Holder" value={bankDetails.accountHolder} fieldId="accountHolder" />}
        {bankDetails?.accountNumber && <CopyableField label="Account Number" value={bankDetails.accountNumber} fieldId="accountNumber" />}
        {bankDetails?.routingNumber && <CopyableField label="Routing Number" value={bankDetails.routingNumber} fieldId="routingNumber" />}
        {bankDetails?.swift && <CopyableField label="Swift Code" value={bankDetails.swift} fieldId="swift" />}
        {bankDetails?.iban && <CopyableField label="IBAN" value={bankDetails.iban} fieldId="iban" />}
        {referenceNumber && <CopyableField label="Payment Reference" value={referenceNumber} fieldId="referenceNumber" />}
      </Box>
    </Card>
  );
};
